import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["options"],
  _excluded2 = ["options"];
import { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { debounce, difference } from 'underscore';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { Link } from 'foundations-components/transitional/components/Link';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { FONT_FORMAT_LIST as SYSTEM_FONTS, FONT_SETS, UNSUPPORTED_FONTS } from 'ContentUtils/constants/Fonts';
import { ASYNC_SELECTOR_DEBOUNCE, DEFAULT_PAGE_SIZE } from 'ContentComponents/components/cmv2/constants';
import * as RequestStatus from 'ContentUtils/constants/RequestStatus';
import { FONT } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { combineFonts, getDefaultVariant } from 'ContentComponents/components/cmv2/fontPicker/utils';
import AsyncSelector from 'ContentComponents/components/cmv2/components/AsyncSelector';
import GoogleFontOption from 'ContentComponents/components/cmv2/fontPicker/GoogleFontOption';
import CustomFontOption from 'ContentComponents/components/cmv2/fontPicker/CustomFontOption';
import { FontOption as DefaultFontOption } from './FontOption';
import BrandingCustomFontOption from 'ContentComponents/components/cmv2/fontPicker/BrandingCustomFontOption';
import * as FontsActions from 'ContentData/actions/Fonts';
import * as fontsSelectors from 'ContentData/selectors/fonts';
import { Button } from 'foundations-components/transitional/components/Button';
import { View } from 'foundations-components/transitional/components/View';
import { getBrandKitId, getBrandSettingsFetchStatus, getBusinessUnitId } from 'ContentData/selectors/branding';
import Routes from 'ContentUtils/Routes';
import { isNullOrUndefined } from 'ContentUtils/helpers/ObjectHelpers';
import * as BrandSettingsActions from 'ContentData/actions/Branding';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = key => `ContentComponents.cmv2.fields.font.${key}`;
const PICKER_MIN_WIDTH = 234;
const flattenNestedObjectArray = (newFlatArray, fontList) => {
  // fontList is a nested array of objects, `options` can either be true, or another fontList array (simplified):
  // [
  //   { options: true, text: 'FontName', value: 'FontFamilyList' },
  //   {
  //     text: 'TitleOfSubgroup',
  //     options: [{ options: true, text: 'FontName', value: 'FontFamilyList' }, {...}],
  //   },
  // ]

  if (fontList === undefined || fontList === true) return newFlatArray;
  if (Array.isArray(fontList)) {
    return fontList.reduce(flattenNestedObjectArray, newFlatArray);
  }
  const {
      options
    } = fontList,
    rest = _objectWithoutPropertiesLoose(fontList, _excluded);
  if (!Array.isArray(options)) {
    newFlatArray.push(rest);
  }
  return flattenNestedObjectArray(newFlatArray, options);
};
const mapStateToProps = (state, props) => {
  const {
    modulePath,
    themePath,
    childTheme
  } = props;
  const assetPath = modulePath ? childTheme || modulePath.replace(/^\/+/, '') : themePath;
  return {
    assetPath,
    brandKitId: getBrandKitId(state, props),
    businessUnitId: getBusinessUnitId(state, props),
    commonGoogleFontResults: fontsSelectors.getCommonGoogleFontResults(state),
    commonGoogleFonts: fontsSelectors.getCommonGoogleFonts(state),
    commonGoogleFontsRequestStatus: fontsSelectors.getCommonFontsFetchStatus(state),
    customFontByAssetOptions: fontsSelectors.getCustomFontsByAssetOptions(state, {
      assetPath
    }),
    customFontOptions: fontsSelectors.getCustomFontsOptions(state),
    customFontsByAsset: fontsSelectors.getCustomFontsByAsset(state, {
      assetPath
    }),
    customFontsByAssetRequestStatus: fontsSelectors.getCustomFontsByAssetFetchStatus(state, {
      assetPath
    }),
    customFonts: fontsSelectors.getCustomFonts(state),
    brandFonts: fontsSelectors.getBrandFonts(state),
    brandFontsRequestStatus: fontsSelectors.getBrandFontsFetchStatus(state),
    brandFontOptions: fontsSelectors.getBrandFontOptions(state),
    customFontsRequestStatus: fontsSelectors.getCustomFontsFetchStatus(state),
    googleFontOptions: fontsSelectors.getOptions(state),
    googleFonts: fontsSelectors.getGoogleFonts(state),
    googleFontsPagination: fontsSelectors.getGoogleFontsPagination(state),
    googleFontsRequestStatus: fontsSelectors.getGoogleFontsFetchStatus(state),
    selectedCustomFonts: fontsSelectors.getSelectedCustomFonts(state),
    selectedGoogleFonts: fontsSelectors.selectedGoogleFonts(state),
    brandSettingsFetchStatus: getBrandSettingsFetchStatus(state)
  };
};
const mapDispatchToProps = {
  fetchCustomFontByAsset: FontsActions.fetchCustomFontByAsset,
  fetchCustomFont: FontsActions.fetchCustomFont,
  fetchCustomFontsByAsset: FontsActions.fetchCustomFontsByAsset,
  fetchCustomFonts: FontsActions.fetchCustomFonts,
  fetchBrandFonts: FontsActions.fetchBrandFonts,
  fetchGoogleFont: FontsActions.fetchFont,
  fetchGoogleFonts: FontsActions.fetchGoogleFonts,
  clearGoogleFonts: FontsActions.clearFonts,
  selectCustomFont: FontsActions.selectCustomFont,
  selectGoogleFont: FontsActions.selectFont,
  fetchCommonGoogleFonts: FontsActions.fetchCommonFonts,
  fetchBusinessUnit: BrandSettingsActions.fetchBusinessUnit,
  fetchBrandKit: BrandSettingsActions.fetchBrandKit
};
const connector = connect(mapStateToProps, mapDispatchToProps);
const SYSTEM_FONT_VALUES = Object.values(SYSTEM_FONTS || {});
const UNSUPPORTED_FONT_VALUES = Object.values(UNSUPPORTED_FONTS || {});
class FontFamilyPicker extends Component {
  constructor(props) {
    super(props);
    this.getLimitedOptions = limitedOptions => {
      const {
        customFontByAssetOptions,
        customFontOptions,
        googleFontOptions,
        showCustomFonts
      } = this.props;
      const combinedCustomFontOptions = [...customFontByAssetOptions, ...customFontOptions];
      const combinedFontOptions = [...(showCustomFonts ? combinedCustomFontOptions : []), ...googleFontOptions, ...this.systemFonts];
      const optionMap = combinedFontOptions.reduce((hash, font) => Object.assign({}, hash, {
        [font.text]: font
      }), {});
      const validOptions = limitedOptions.reduce((hash, fontName) => {
        const validOption = optionMap[fontName];
        /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
        return validOption ? [...hash, validOption] : hash;
      }, []);
      return validOptions;
    };
    if (props.fontList) {
      this.fontListArray = flattenNestedObjectArray([], Object.values(props.fontList));
    }
    this.getFontListOption = this.getFontListOption.bind(this);
    this.loadOptions = this.loadOptions.bind(this);
    this.onFontSelect = this.onFontSelect.bind(this);
    this.renderDisplayFont = this.renderDisplayFont.bind(this);
    this.renderFontPickerOption = this.renderFontPickerOption.bind(this);
    this.renderFontPickerValue = this.renderFontPickerValue.bind(this);
    this.trackCustomFontSelect = this.trackCustomFontSelect.bind(this);
    this.debouncedLoadOptionsWithFontFetching = debounce(this.loadOptionsWithFontFetching, ASYNC_SELECTOR_DEBOUNCE);
    this.debouncedLoadLimitedOptionsWithFontFetching = debounce(this.loadLimitedOptionsWithFontFetching, ASYNC_SELECTOR_DEBOUNCE);
    this.fontPickerRef = /*#__PURE__*/createRef();
    this.state = {
      input: ''
    };
  }
  componentDidMount() {
    const {
      brandKitId,
      businessUnitId,
      fetchBusinessUnit,
      fetchBrandKit,
      contentCategoryName,
      contentId
    } = this.props;

    // get brandKitId
    if (this.shouldFetchBrandingData && isNullOrUndefined(brandKitId)) {
      if (isNullOrUndefined(businessUnitId) && contentCategoryName && contentId) {
        fetchBusinessUnit(contentCategoryName, contentId).catch(err => {
          throw err;
        });
      } else {
        fetchBrandKit(businessUnitId).catch(err => {
          throw err;
        });
      }
    }
    this.fetchSelectedFont();
  }
  componentDidUpdate(prevProps) {
    const {
      businessUnitId,
      fetchBrandKit,
      brandSettingsFetchStatus,
      value
    } = this.props;
    const {
      businessUnitId: prevBusinessUnitId,
      brandSettingsFetchStatus: prevBrandSettingsFetchStatus,
      value: prevValue
    } = prevProps;
    if (this.shouldFetchBrandingData && businessUnitId !== prevBusinessUnitId) {
      fetchBrandKit(businessUnitId).catch(err => {
        throw err;
      });
    }
    if (brandSettingsFetchStatus === RequestStatus.SUCCEEDED && prevBrandSettingsFetchStatus !== RequestStatus.SUCCEEDED && prevValue.font !== value.font) {
      this.fetchSelectedFont();
    }
  }
  get seleniumKey() {
    return this.props.fieldKey.join('.');
  }
  get systemFonts() {
    return Object.keys(SYSTEM_FONTS).map(fontKey => {
      return {
        fontSet: FONT_SETS.DEFAULT,
        text: fontKey,
        value: SYSTEM_FONTS[fontKey]
      };
    });
  }
  get commonGoogleFontOptions() {
    const {
      commonGoogleFontResults
    } = this.props;
    const googleFontOptions = commonGoogleFontResults.map(font => {
      const {
        family
      } = font;
      return {
        fontSet: FONT_SETS.GOOGLE,
        text: family,
        value: family
      };
    });
    return combineFonts({
      googleFontOptions,
      systemFonts: this.systemFonts
    });
  }
  get allGoogleFonts() {
    return Object.assign({}, this.props.commonGoogleFonts, this.props.googleFonts, this.props.selectedGoogleFonts);
  }
  get defaultOptions() {
    const {
      value
    } = this.props;
    const {
      font_set
    } = value || {};
    let unsupportedFont = {};
    if (this.fontListArray) {
      return this.fontListArray.map(this.getFontListOption);
    } else if (!SYSTEM_FONT_VALUES.includes(this.font) && !UNSUPPORTED_FONT_VALUES.includes(this.font)) {
      return [{
        fontSet: font_set,
        text: this.font,
        value: this.font
      }];
    }
    if (UNSUPPORTED_FONT_VALUES.includes(this.font)) {
      const unsupportedFontName = Object.keys(UNSUPPORTED_FONTS).find(fontKey => UNSUPPORTED_FONTS[fontKey] === this.font);
      unsupportedFont = {
        text: unsupportedFontName,
        value: this.font
      };
    }
    return [unsupportedFont, ...this.systemFonts];
  }
  get font() {
    const {
      value
    } = this.props;
    return value && value.font ? value.font : null;
  }
  get shouldFetchBrandingData() {
    const {
      showBrandFonts,
      showCustomFonts
    } = this.props;
    return showBrandFonts || showCustomFonts;
  }
  get shouldShowBrandFonts() {
    const {
      showBrandFonts,
      showCustomFonts
    } = this.props;
    return showBrandFonts && showCustomFonts;
  }
  fetchSelectedFont() {
    const {
      assetPath,
      fetchCustomFontByAsset,
      fetchCustomFont,
      fetchCustomFonts,
      fetchGoogleFont,
      value
    } = this.props;
    const {
      font,
      font_set,
      fontId
    } = value || {};
    if (font && !SYSTEM_FONT_VALUES.includes(font) && !UNSUPPORTED_FONT_VALUES.includes(font)) {
      let fontName = font;
      if (this.fontListArray) {
        const queryFont = this.fontListArray.find(fontObject => fontObject.value === font);
        fontName = queryFont && queryFont.text || font;
      }
      if (font_set === FONT_SETS.CUSTOM) {
        fetchCustomFontByAsset({
          family: font,
          path: assetPath
        }).catch(err => {
          throw err;
        });
      } else if (font_set === FONT_SETS.BRANDING) {
        if (fontId) {
          fetchCustomFont(fontId).catch(err => {
            throw err;
          });
        } else {
          // if a BRANDING font is selected but there is no fontId in the value object,
          // fallback to just fetch all branding fonts so picker can find selected font data
          // this can happen with inherited values using property paths and id is not included
          fetchCustomFonts({
            selectedFontName: font
          }).catch(err => {
            throw err;
          });
        }
      } else if (font_set === FONT_SETS.GOOGLE) {
        fetchGoogleFont(fontName).catch(err => {
          throw err;
        });
      }
    }
  }
  trackCustomFontSelect(fontFamily, customFontType) {
    const {
      trackFieldAction
    } = this.props;
    if (trackFieldAction) {
      trackFieldAction('custom-fonts-select-font', {
        fontFamily,
        customFontType
      });
    }
  }
  getFontListOption(fontListOption) {
    const {
      selectedCustomFonts
    } = this.props;
    if (fontListOption.options) return fontListOption;
    let fontSet;
    if (SYSTEM_FONTS[fontListOption.text]) {
      fontSet = FONT_SETS.DEFAULT;
    } else if (selectedCustomFonts[fontListOption.text]) {
      fontSet = FONT_SETS.CUSTOM;
    } else {
      fontSet = FONT_SETS.GOOGLE;
    }
    return {
      fontSet,
      text: fontListOption.text,
      value: fontListOption.value,
      buttonText: fontListOption.buttonText
    };
  }
  getGroupedOptions(input) {
    const {
      customFontByAssetOptions,
      customFontOptions,
      googleFontOptions,
      showCustomFonts,
      brandFontOptions,
      googleFontsPagination
    } = this.props;

    // TODO This combines old and new kinds of custom fonts so that old theme dev added is just
    // sitting on top of marketer branding uploads as one list under Custom Fonts group.
    // Do we want to separate the different kinds of custom fonts into their own groups?
    // Do we wnat to add any sorting like aphabetical (now it is most->least recently added, endpoint default)
    // Also is it weird that only the marketer uploaded fonts appear in the custom font modal manage page
    // if they are group together like this in the picker?
    // Should we limit number of custom fonts loaded and use load more like google fonts?
    const combinedCustomFontOptions = customFontByAssetOptions && customFontByAssetOptions.concat(customFontOptions);
    const allFontOptions = combineFonts(Object.assign({
      googleFontOptions,
      systemFonts: this.systemFonts
    }, showCustomFonts && {
      customFontOptions: combinedCustomFontOptions
    }), googleFontsPagination.hasMore, input);
    let options = [];
    if (!input || !input.length) {
      options = [{
        text: unescapedText(i18nKey('commonFonts')),
        options: this.commonGoogleFontOptions
      }, {
        text: unescapedText(i18nKey('allFonts')),
        options: allFontOptions
      }];
      if (showCustomFonts) {
        options.unshift({
          text: unescapedText(i18nKey('customFonts')),
          options: combinedCustomFontOptions
        });
      }
      if (this.shouldShowBrandFonts) {
        options.unshift({
          text: unescapedText(i18nKey('brandFonts')),
          options: brandFontOptions
        });
      }
    } else {
      options = allFontOptions;
    }
    return options;
  }
  loadOptionsWithFontFetching(input, callback, pagination, shouldOnlyRefetchCustomFonts) {
    const {
      assetPath,
      brandKitId,
      clearGoogleFonts,
      commonGoogleFontsRequestStatus,
      fetchCommonGoogleFonts,
      fetchCustomFontsByAsset,
      fetchCustomFonts,
      fetchBrandFonts,
      fetchGoogleFonts,
      googleFontsPagination
    } = this.props;
    let offset = pagination.offset;
    if (input !== this.state.input) {
      this.setState({
        input
      });
      clearGoogleFonts();
      offset = 0;
    }
    const query = {
      search: input,
      limit: DEFAULT_PAGE_SIZE,
      offset
    };
    const customFontRequest = fetchCustomFonts({
      brandKitId
    });
    let fontRequests;
    if (shouldOnlyRefetchCustomFonts) {
      fontRequests = [customFontRequest];
    } else {
      const googleFontsRequest = fetchGoogleFonts(query);
      const customFontByAssetRequest = fetchCustomFontsByAsset({
        path: assetPath
      });
      const commonFontsRequest = commonGoogleFontsRequestStatus === RequestStatus.UNINITIALIZED ? fetchCommonGoogleFonts({
        common: true
      }) : Promise.resolve();
      const brandFontsRequest = this.shouldShowBrandFonts && fetchBrandFonts(brandKitId);
      fontRequests = [googleFontsRequest, customFontByAssetRequest, commonFontsRequest, customFontRequest, brandFontsRequest];
    }
    Promise.all(fontRequests).then(([queriedResponse]) => {
      const {
        total,
        hasMore
      } = queriedResponse;
      const {
        total: savedTotal,
        hasMore: savedHasMore,
        offset: savedOffset
      } = googleFontsPagination;
      const options = this.getGroupedOptions(input);
      callback(null, {
        options,
        pagination: {
          hasMore: hasMore || savedHasMore,
          offset: (offset || savedOffset) + DEFAULT_PAGE_SIZE,
          length: total || savedTotal
        }
      });
    }).catch(err => {
      throw err;
    });
  }
  loadLimitedOptionsWithFontFetching(limitedOptions, callback) {
    const {
      assetPath,
      brandKitId,
      fetchCustomFontsByAsset,
      fetchCustomFonts,
      fetchGoogleFont
    } = this.props;
    const customFontByAssetRequest = fetchCustomFontsByAsset({
      path: assetPath
    });
    const customFontRequest = fetchCustomFonts({
      brandKitId
    });
    Promise.all([customFontRequest, customFontByAssetRequest]).then(([queriedResponse]) => {
      //filter out non google fonts from the limited options
      const fetchedFontNames = queriedResponse.map(font => font.name || font.family);
      const googleOptions = difference(limitedOptions, Object.keys(SYSTEM_FONTS), fetchedFontNames);

      //fetch google font options
      if (googleOptions.length) {
        const googleFontRequests = googleOptions.map(option => fetchGoogleFont(option));
        Promise.all(googleFontRequests).then(() => {
          callback(null, {
            options: this.getLimitedOptions(limitedOptions)
          });
        }).catch(err => {
          throw err;
        });
      } else {
        callback(null, {
          options: this.getLimitedOptions(limitedOptions)
        });
      }
    }).catch(err => {
      throw err;
    });
  }
  loadOptions(input, callback, pagination) {
    const {
      assetPath,
      customFontsByAssetRequestStatus,
      customFontsRequestStatus,
      fontList,
      googleFontOptions,
      isEmailOrAnyType,
      limitedOptions,
      shouldRefetchCustomFonts,
      googleFontsRequestStatus,
      googleFontsPagination
    } = this.props;
    if (this.fontListArray) {
      const fontOptions = Object.values(fontList).reduce((fontOptionsAcc, fontOption) => {
        const {
            options
          } = fontOption,
          rest = _objectWithoutPropertiesLoose(fontOption, _excluded2);

        // support 1-level grouped options
        if (Array.isArray(options)) {
          fontOptionsAcc.push({
            text: fontOption.text,
            options: options.map(this.getFontListOption)
          });
        } else {
          fontOptionsAcc.push(this.getFontListOption(rest));
        }
        return fontOptionsAcc;
      }, []);
      callback(null, {
        options: fontOptions
      });
      return;
    }
    if (isEmailOrAnyType) {
      callback(null, {
        options: this.systemFonts
      });
      return;
    }
    let offset = pagination.offset;

    // If you load a few pages of Google fonts and then close/reopen the select,
    // don't refetch any Google fonts. But if there's custom fonts to fetch,
    // we must do a refetch
    const inputHasNotChanged = input === this.state.input;
    const numGoogleFontsFetched = Object.keys(googleFontOptions).length;
    const hasAlreadyFetchedGoogleFonts = googleFontsRequestStatus === RequestStatus.SUCCEEDED && numGoogleFontsFetched > 1;
    const hasAlreadyFetchedCustomFontsByAsset = !assetPath || customFontsByAssetRequestStatus === RequestStatus.SUCCEEDED;
    const hasAlreadyFetchedCustomFonts = customFontsRequestStatus === RequestStatus.SUCCEEDED;
    const shouldNotRefetchFontsGated = inputHasNotChanged && !offset && hasAlreadyFetchedGoogleFonts && hasAlreadyFetchedCustomFontsByAsset;
    const shouldNotRefetchFonts = shouldNotRefetchFontsGated && hasAlreadyFetchedCustomFonts;
    const shouldOnlyRefetchCustomFonts = shouldNotRefetchFonts && shouldRefetchCustomFonts;
    if (shouldNotRefetchFonts && !shouldRefetchCustomFonts) {
      offset = numGoogleFontsFetched;
      if (limitedOptions) {
        callback(null, {
          options: this.getLimitedOptions(limitedOptions)
        });
      } else {
        const {
          total: savedTotal,
          hasMore: savedHasMore,
          offset: savedOffset
        } = googleFontsPagination;
        callback(null, {
          options: this.getGroupedOptions(input),
          pagination: {
            hasMore: savedHasMore,
            offset: offset || savedOffset,
            length: savedTotal
          }
        });
      }
      return;
    }
    if (limitedOptions) {
      this.debouncedLoadLimitedOptionsWithFontFetching(limitedOptions, callback);
    } else {
      this.debouncedLoadOptionsWithFontFetching(input, callback, pagination, shouldOnlyRefetchCustomFonts);
    }
  }
  onFontSelect({
    target: {
      value: font
    }
  }) {
    const {
      customFontsByAsset,
      customFonts,
      fontList,
      onFontFamilyChange,
      selectCustomFont,
      selectGoogleFont,
      fetchGoogleFont
    } = this.props;
    let newFontObject = {
      font,
      fallback: null,
      variant: null,
      font_set: FONT_SETS.DEFAULT
    };
    let fontFamilyReadyToBeChanged = true;
    if (!fontList && !SYSTEM_FONT_VALUES.includes(font)) {
      const customFontByAsset = customFontsByAsset[font];
      const customFont = customFonts[font];
      if (customFontByAsset) {
        selectCustomFont(customFontByAsset);
        const {
          variants,
          family,
          fallback,
          styles = {}
        } = customFontByAsset;
        this.trackCustomFontSelect(family, FONT_SETS.CUSTOM);
        const defaultVariant = variants[0];
        newFontObject = {
          styles: styles[defaultVariant],
          variant: defaultVariant,
          font: family,
          fallback,
          font_set: FONT_SETS.CUSTOM
        };
      } else if (customFont) {
        var _ref, _variants$;
        selectCustomFont(customFont);
        const {
          variants,
          name,
          fallback,
          id: fontId
        } = customFont;
        this.trackCustomFontSelect(name, FONT_SETS.BRANDING);
        const defaultVariant = (_ref = variants && ((_variants$ = variants[0]) === null || _variants$ === void 0 ? void 0 : _variants$.name)) !== null && _ref !== void 0 ? _ref : '';
        const styles = variants && variants.reduce((acc, variant) => {
          const {
            style,
            name: variantName
          } = variant;
          try {
            const styleObject = style && typeof style === 'string' ? JSON.parse(style) : {};
            acc[variantName] = styleObject;
          } catch (e) {
            console.error('Error parsing style object', e);
          }
          return acc;
        }, {});
        newFontObject = {
          styles: styles && styles[defaultVariant],
          variant: defaultVariant,
          font: name,
          fallback,
          font_set: FONT_SETS.BRANDING,
          fontId
        };
      } else {
        const googleFont = this.allGoogleFonts[font];
        if (googleFont) {
          selectGoogleFont(googleFont);
          const {
            variants,
            family,
            fallback
          } = googleFont;
          newFontObject = {
            styles: {},
            variant: getDefaultVariant(variants),
            font: family,
            fallback,
            font_set: FONT_SETS.GOOGLE
          };
        } else {
          // if the font is not found in the google fonts, we need to fetch it first
          fontFamilyReadyToBeChanged = false;
          fetchGoogleFont(font).then(fetchedFont => {
            selectGoogleFont(fetchedFont);
            const {
              variants,
              family,
              fallback
            } = fetchedFont;
            newFontObject = {
              styles: {},
              variant: getDefaultVariant(variants),
              font: family,
              fallback,
              font_set: FONT_SETS.GOOGLE
            };
            onFontFamilyChange(newFontObject);
          }).catch(err => {
            throw err;
          });
        }
      }
    }
    if (fontFamilyReadyToBeChanged) {
      onFontFamilyChange(newFontObject);
    }
  }
  renderDisplayFont(option) {
    const {
      commonGoogleFonts,
      customFontsByAsset,
      customFonts,
      fontList,
      googleFonts,
      selectedCustomFonts,
      selectedGoogleFonts
    } = this.props;
    const {
      buttonText,
      dropdownText,
      fontSet,
      text,
      value
    } = option;
    if (buttonText || dropdownText) return buttonText || dropdownText;
    const fontKey = fontList ? text : value;
    switch (fontSet) {
      case FONT_SETS.BRANDING:
        {
          const customFont = selectedCustomFonts[fontKey] || customFonts[fontKey];
          return /*#__PURE__*/_jsx(BrandingCustomFontOption, {
            font: customFont || {
              family: fontKey
            }
          });
        }
      case FONT_SETS.CUSTOM:
        {
          const customFont = selectedCustomFonts[fontKey] || customFontsByAsset[fontKey];
          return /*#__PURE__*/_jsx(CustomFontOption, {
            font: customFont || {
              family: fontKey
            }
          });
        }
      case FONT_SETS.GOOGLE:
        {
          const googleFont = selectedGoogleFonts[fontKey] || commonGoogleFonts[fontKey] || googleFonts[fontKey];
          return /*#__PURE__*/_jsx(GoogleFontOption, {
            font: googleFont || {
              family: fontKey
            }
          });
        }
      case FONT_SETS.DEFAULT:
        {
          const systemFont = SYSTEM_FONTS[text];
          return /*#__PURE__*/_jsx(DefaultFontOption, {
            fontFamily: systemFont,
            children: text
          });
        }
      default:
        return /*#__PURE__*/_jsx(DefaultFontOption, {
          fontFamily: value,
          children: text || value
        });
    }
  }
  renderFontPickerOption(option) {
    const {
      options,
      text,
      value,
      dropdownText,
      loading
    } = option;
    const isOptionGroupHeader = Array.isArray(options);
    const optionText = text || value;
    if (loading) {
      return /*#__PURE__*/_jsx(View, {
        paddingBlock: "200",
        children: dropdownText
      });
    }
    return /*#__PURE__*/_jsx("div", {
      "data-selenium-option": optionText,
      "data-option-value": value,
      role: "option",
      title: optionText,
      children: /*#__PURE__*/_jsx(Button, {
        tabIndex: -1,
        use: "unstyled",
        paddingBlock: "200",
        paddingInline: "500",
        children: /*#__PURE__*/_jsx(TFlex, {
          width: "fit-content",
          children: isOptionGroupHeader ? optionText : this.renderDisplayFont(option)
        })
      })
    });
  }
  renderFontPickerValue(option) {
    return /*#__PURE__*/_jsx("span", {
      "data-option-value": option.value,
      role: "option",
      children: /*#__PURE__*/_jsx("span", {
        "data-option-text": true,
        children: this.renderDisplayFont(option)
      })
    });
  }
  renderFooterLinks(isEmailOrCustomFonts) {
    const {
      limitedOptions
    } = this.props;

    // no footer links at all
    if (limitedOptions || isEmailOrCustomFonts) return;
    const googleFontsLink = /*#__PURE__*/_jsx(Text, {
      as: "small",
      children: /*#__PURE__*/_jsx(Link, {
        external: true,
        href: "https://fonts.google.com/",
        "aria-label": unescapedText(i18nKey('googleFontsLink')),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('googleFontsLink')
        })
      })
    });
    const manageBrandFontsLink = /*#__PURE__*/_jsx(Text, {
      as: "small",
      children: /*#__PURE__*/_jsx(Link, {
        external: true,
        href: Routes.settings('brand-identity'),
        "data-selenium": `${this.seleniumKey}:manageBrandFontsLink`,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('manageBrandFontsLink')
        })
      })
    });
    const secondLink = this.shouldShowBrandFonts ? manageBrandFontsLink : null;
    return secondLink ? /*#__PURE__*/_jsxs(List, {
      children: [/*#__PURE__*/_jsx(ListItem, {
        children: googleFontsLink
      }), /*#__PURE__*/_jsx(ListItem, {
        marginBlockStart: "200",
        children: secondLink
      })]
    }) : googleFontsLink;
  }
  render() {
    var _this$fontPickerRef;
    const {
      error,
      value,
      readOnly,
      fontPickerProps,
      isEmailOrAnyType,
      fontList,
      useOnDark,
      limitedOptions
    } = this.props;
    const {
      font
    } = value || {};
    const isEmailOrCustomFonts = isEmailOrAnyType || !!fontList;
    const fontPickerWidth = (_this$fontPickerRef = this.fontPickerRef) === null || _this$fontPickerRef === void 0 || (_this$fontPickerRef = _this$fontPickerRef.current) === null || _this$fontPickerRef === void 0 || (_this$fontPickerRef = _this$fontPickerRef.getBoundingClientRect()) === null || _this$fontPickerRef === void 0 ? void 0 : _this$fontPickerRef.width;
    return /*#__PURE__*/_jsx("div", {
      ref: this.fontPickerRef,
      children: /*#__PURE__*/_jsx(AsyncSelector, Object.assign({}, fontPickerProps, {
        "data-selenium": `${this.seleniumKey}:font`,
        defaultOptions: this.defaultOptions,
        dropdownFooter: this.renderFooterLinks(isEmailOrCustomFonts),
        error: UNSUPPORTED_FONT_VALUES.includes(font) || error,
        loadOptions: this.loadOptions,
        menuWidth: fontPickerWidth && fontPickerWidth < PICKER_MIN_WIDTH ? PICKER_MIN_WIDTH : undefined,
        menuStyles: {
          maxBlockSize: '325px'
        },
        onChange: this.onFontSelect,
        optionRenderer: this.renderFontPickerOption,
        placeholder: unescapedText(i18nKey('defaultButtonText')),
        readOnly: readOnly,
        searchable: !limitedOptions && !isEmailOrCustomFonts,
        type: FONT,
        useOnDark: useOnDark,
        value: this.font,
        valueRenderer: this.renderFontPickerValue,
        fixedHeight: "40px"
      }))
    });
  }
}
FontFamilyPicker.defaultProps = {
  showCustomFonts: true,
  showBrandFonts: true,
  shouldRefetchCustomFonts: false
};
export default connector(FontFamilyPicker);